import { makeDatesRelative } from '@ts/utils/relativeDate';

/**
 * -----------------------
 * Resctriction by time of day
 */
export const allowedTime = {
  startTime: 7,
  endTime: 22,
};

export const isCommentAllowed = () => {
  const currentHour = new Date().getHours();

  return (
    currentHour >= allowedTime.startTime && currentHour < allowedTime.endTime
  );
};

/**
 * TODO: fix highlight and animateAnchor functions
 * -----------------------
 * Highlight animation
 */
const highlight = (element: HTMLElement) => {
  element.classList.add('highlighted');
  element.addEventListener('animationend', () =>
    element.classList.remove('highlighted'),
  );
};

export const animateAnchor = async (target: string, offset_: number = 0) => {
  const targetElement = document.querySelector<HTMLElement>(target);
  if (!targetElement) return;

  const { parentElement } = targetElement;

  let offset = 0;

  if (parentElement?.classList.contains('js-comment-container')) {
    offset = parentElement.getBoundingClientRect().top + window.scrollY; // offset for comments
    await new Promise<void>(resolve => {
      window.scrollTo({ top: offset, behavior: 'smooth' });
      setTimeout(() => {
        highlight(targetElement);
        resolve();
      }, 2000);
    });
  } else if (targetElement) {
    offset =
      targetElement.getBoundingClientRect().top + window.scrollY - offset_; // all other anchors
    await new Promise<void>(resolve => {
      window.scrollTo({ top: offset, behavior: 'smooth' });
      setTimeout(() => resolve(), 2000);
    });
  }
};

/**
 * -----------------------
 * Styling after comment loading
 */
const removeFormRequiredAttr = () => {
  document
    .querySelector('#form-widgets-comment-text')
    ?.removeAttribute('required');
};

const commentAntecessor = () => {
  const replyToElements =
    document.querySelectorAll<HTMLElement>('.js-reply-writer');

  // Iterate over all replies to existing comments and generate the reference to the comment they are replying (if any).
  replyToElements.forEach(reply => {
    // Find the comment to the reply
    const replyTo = document
      .getElementById(reply.getAttribute('data-in_reply_to') || '')
      ?.closest('.js-comment-wrapper');

    // Get original comment's author's name
    const originalAuthor =
      replyTo?.querySelector('.js-origin-writer')?.textContent;

    // Fill out the link that references the original author's comment
    if (originalAuthor) {
      reply.textContent = `@${originalAuthor}`;
      reply.classList.add('bc-comment-card__reply-to', 't-author--reference');
    } else {
      const { parentElement } = reply;
      if (parentElement) {
        parentElement.outerHTML = `<span class="bc-comment-card__hidden">
          @(versteckter Kommentar)
          </span>`;
      }
    }
  });
};

const toggleZippedComment = () => {
  const zippedComments = document.querySelectorAll('.js-comment-zipped');

  zippedComments?.forEach(comment => {
    comment.addEventListener('click', event => {
      const { target } = event;
      if (!(target instanceof HTMLElement)) return;

      const commentBox = target
        .closest('.js-comment-wrapper')
        ?.querySelector('.js-comment-box');

      commentBox?.classList.toggle('t-comment-card--sliding-down');
      commentBox?.classList.toggle('t-comment-card--sliding-up');

      const expandClass = 'bc-comment-card__notice--expand';
      const hideClass = 'bc-comment-card__notice--hide';

      target.classList.toggle(expandClass);
      target.classList.toggle(hideClass);
    });
  });
};

const dropdownAction = () => {
  const actionButtons = document.querySelectorAll('.js-dropdown-action');

  actionButtons.forEach(actionButton => {
    actionButton.addEventListener('click', () => {
      const dropdownList = actionButton.nextElementSibling as HTMLUListElement;

      actionButton.classList.toggle('is-dropdown-open');
      dropdownList.classList.toggle('is-dropdown-open');
    });
  });
};

// TODO: check function when feature is live
// potentially move api call to service layer
/**
 * Load the comment flag form below the comment the user wants to flag.
 * The event handler uses the namespaced event 'click.showForm' to not have
 * side effect on other events.
 */
const showFlagCommentForm = () => {
  document
    .querySelector('.js-comment-flag')
    ?.addEventListener('click.showForm', async e => {
      e.preventDefault(); // Prevent following the link directly.

      const query: Record<string, string> = {};
      const currentTarget = e.currentTarget as HTMLElement;

      const commentId = currentTarget.dataset.commentid;
      const formSelector = document.querySelector(
        `.js-comment-flag-form-[data-commentid="${commentId}"]`,
      );

      // Add a placeholder to put the form in
      const flagFormPlaceholder = document.createElement('div');
      flagFormPlaceholder.className = 'flag-form';

      if (formSelector) formSelector.after(flagFormPlaceholder);

      // URL that will be ajax loaded
      const target = currentTarget.getAttribute('href');
      if (target) {
        query.ajax_load = '1';

        // Give quick user feedback that the flag form is being loaded
        const loadingElement = document.querySelector('.js-loading');
        loadingElement?.remove();
        const loadingMessage = document.createElement('p');
        loadingMessage.className = 'c-flag-comment-form__loading js-loading';
        loadingMessage.textContent = 'Das Formular lädt...';
        formSelector?.after(loadingMessage);

        // Load the form
        try {
          const response = await fetch(`${target} #flag-comment-form`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(query),
          });

          if (!response.ok) {
            loadingMessage.textContent =
              'Das Formular konnte nicht geladen werden.';
            return;
          }

          // The form was successfully loaded, there is no need to show the loading info
          loadingMessage.remove();

          // Cleanup, styling and text fixes
          flagFormPlaceholder.classList.add('bc-comment-form');

          // Remove reply button
          const replyContainer = document.querySelector(
            `#${commentId} div.js-comment-reply-container`,
          );
          replyContainer?.remove();
        } catch (error) {
          if (error)
            loadingMessage.textContent =
              'Das Formular konnte nicht geladen werden.';
        }
      }
    });
};

export const afterLoadingComments = () => {
  removeFormRequiredAttr();

  commentAntecessor();
  toggleZippedComment();
  showFlagCommentForm();
  makeDatesRelative();
  dropdownAction();
};
